import React from 'react';
import { Loader2 } from 'lucide-react';
import DeliverablePlanCard from './DeliverablePlanCard';
import PremiumLiveActivity from './PremiumLiveActivity';
import Button from '../../../../components/Button';
import type { BrandTeamSession, BrandTeamAgentLog } from '../../../../services/brandTeamService';

interface StepPlanProps {
  session: BrandTeamSession;
  agentLogs: BrandTeamAgentLog[];
  onApprove: () => void;
  approving: boolean;
  onNext: () => void;
  onShuffle?: (index: number) => void;
  shufflingIndex?: number | null;
}

const StepPlan: React.FC<StepPlanProps> = ({ session, agentLogs, onApprove, approving, onNext, onShuffle, shufflingIndex }) => {
  const plan = session.deliverablePlan || {};
  const categories: Array<Record<string, any>> = Array.isArray(plan.categories) ? plan.categories : [];
  const hasPlan = categories.length > 0;
  const isApproved = !!session.planApprovedAt;

  // Loading state
  if (!hasPlan) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-brand-purple" />
        <h3 className="text-lg font-bold text-text-dark">Mockup Planner is building your deliverable queue...</h3>
        <p className="mt-2 text-sm text-body-grey">Analyzing your brief and selecting optimal templates</p>
        {agentLogs.length > 0 && (
          <div className="mt-8 mx-auto max-w-lg">
            <PremiumLiveActivity logs={agentLogs} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-text-dark">Review Deliverable Plan</h2>
            <p className="mt-1 text-sm text-body-grey">Review the planned mockups before generation.</p>
          </div>
          <div className="rounded-lg bg-primary-50 px-3 py-1.5 text-xs font-semibold text-brand-purple">
            {categories.length} deliverables &middot; Est. {session.creditEstimate || '?'} credits
          </div>
        </div>
      </div>

      <DeliverablePlanCard
        categories={categories}
        status={session.status}
        planApprovedAt={session.planApprovedAt}
        onApprove={onApprove}
        loading={approving}
        onShuffle={onShuffle}
        shufflingIndex={shufflingIndex}
      />

      {/* Bottom action */}
      {isApproved && (
        <div className="mt-8 flex justify-end">
          <Button variant="primary" size="large" onClick={onNext}>
            Continue to Generate →
          </Button>
        </div>
      )}
    </div>
  );
};

export default StepPlan;
